import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';
import packageObject from 'package';
import { filter } from 'rxjs/operators';
import { Application, ApplicationSegment, ApplicationSource, MarketingData, WebappInitializationData, XeroOAuthDTO } from '../models/data-model';
import { AmplitudeService } from './amplitude.service';
import { BundleInfoService } from './bundle-info.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from './dynatraceHelper.service';
import { GoogleTagManagerService } from './google-tag-manager.service';
import { I18nService } from './i18n.service';
import { IdvService } from './idv.service';
import { IntercomIntegrationService } from './intercomIntegration.service';
import { MarketoIntegrationService } from './marketo-integration.service';
import { RegistrationWebappPushApiService } from './registration-webapp-push-api.service';
import { RegistrationService } from './registration.service';
import { SoldoCookieService, SoldoRegistrationConfigData } from "./soldo-cookie.service";
import { XeroIntegrationService, XeroStatus } from './xero-integration.service';

@Injectable()
export class StartupService {

    private readonly initRoutingRules = [
        // Identity Verification with Onfido
        (url: URL, conf: Partial<SoldoRegistrationConfigData>) => {
            const idvProvider = this.getSearchParam(url, "idvProvider", conf.idvProvider, 16);
            const idvSessionId = this.getSearchParam(url, "idvSessionId", conf.idvSessionId);
            if (idvProvider === 'Onfido' && idvSessionId) {
                conf.idvSessionId = idvSessionId;
                this.soldoCookieService.storeConfig(conf);
                this.idvService.setCurrentIdvSessionData({
                    providerName: 'ONFIDO',
                    idvReference: idvSessionId
                });

                return 'static/verify-your-identity';
            }
            return null;
        },
        // Identity Verification with Onfido - Internal users
        (url: URL, conf: Partial<SoldoRegistrationConfigData>) => {
            const sessionToken = this.getSearchParam(url, "token", this.soldoCookieService.readJWT(), 1024);
            const personExternalId = this.getSearchParam(url, "personExternalId", conf.idvPersonExternalId);

            if (sessionToken && personExternalId) {
                conf.idvPersonExternalId = personExternalId;
                this.soldoCookieService.storeJWT(sessionToken);
                this.soldoCookieService.storeConfig(conf);

                this.idvService.setCurrentIdvSessionData({
                    providerName: 'ONFIDO',
                    personExternalId: personExternalId,
                    idvReference: null
                });

                return 'static/internal-user-idv';
            }
            return null;
        },
        // Bypass Email Verification with One-Time-Token
        (url: URL, conf: Partial<SoldoRegistrationConfigData>) => {
            const oneTimeVerificationToken = this.getSearchParam(url, "evt", null, 1024);
            if (oneTimeVerificationToken) {
                this.logger.debug("Redirect to prefilled email verification.");
                this.registrationService.setOneTimeCode(oneTimeVerificationToken);
                this.registrationService.deleteJWT();

                return 'auth/email-verification';
            }
            return null;
        },
        // Static Page
        (url: URL, conf: Partial<SoldoRegistrationConfigData>) => {
            return url.hash.length > 2 ? url.hash.substring(2) : null;
        }
    ];

    constructor(
        private readonly logger: NGXLogger,
        private readonly registrationService: RegistrationService,
        private readonly idvService: IdvService,
        private readonly bundleInfoService: BundleInfoService,
        private readonly soldoCookieService: SoldoCookieService,
        private readonly amplitudeService: AmplitudeService,
        private readonly marketoIntegrationService: MarketoIntegrationService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly pushAPI: RegistrationWebappPushApiService,
        private readonly intercomIntegrationService: IntercomIntegrationService,
        private readonly xeroIntegrationService: XeroIntegrationService,
        private readonly router: Router,
        private readonly dynatrace: DynatraceHelperService,
        private readonly i18nService: I18nService,
        @Inject(APP_CONFIG) private readonly config: AppConfig
    ) { }

    // This is the method you want to call at bootstrap
    // Important: It should return a Promise
    async load(): Promise<void> {

        this.initializeAnalyticsServices();

        const storedConfig = this.soldoCookieService.getStoredConfig();
        const currentUrl = new URL(location.toString());
        this.registrationService.setSegment(this.decodeApplicationSegmentParameter(this.getSearchParam(currentUrl, "s", "s")));
        const offerName: string = this.getSearchParam(currentUrl, "OfferName", null, 32);
        const planName: string = this.getSearchParam(currentUrl, "PlanName", null, 32);
        this.bundleInfoService.setNewApplicationOffer(offerName);
        this.bundleInfoService.setNewApplicationPlan(planName);

        // Check Xero OpenID initialization params
        const xeroStatus = this.xeroIntegrationService.getXeroStatus(currentUrl);
        if (xeroStatus.status === 'Start') {
            return this.initiateSignupWithXero(planName, offerName);
        } else if (xeroStatus.status === 'Auth') {
            return this.startupWithXeroOpenID(xeroStatus).then((resumeRoute) => {
                this.recordCookiePreferences();
                this.setInitialNavigation(resumeRoute);
            });
        }

        // Scan other initialization rules to determine initial navigation
        let initRoute: string = null;
        for(let r = 0; r < this.initRoutingRules.length; r++) {
            const initRouteRule = this.initRoutingRules[r];
            initRoute = initRouteRule(currentUrl, storedConfig);
            if(initRoute !== null) {
                break;
            }
        }

        if (/^static\/.+/.test(initRoute)) {

            this.logger.debug("Rendering a static route. Skipping Registration flow init.", initRoute);
            this.setInitialNavigation(initRoute);
            return;

        } else {

            this.intercomIntegrationService.init();
            this.registrationService.setMarketingData(this.initializeMarketingData(currentUrl));
            return this.initRegistrationFlow(currentUrl, storedConfig, planName, offerName).then((r) => {
                this.recordCookiePreferences();
                return r;
            });

        }
    }

    private async initRegistrationFlow(currentUrl: URL, storedConfig: Partial<SoldoRegistrationConfigData>, planName: string, offerName: string): Promise<void> {
        let applications: Application[];
        const startupActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.STARTUP_INIT_REGISTRATION);

        try {
            applications = await this.registrationService.getApplicationList(false);
        } catch (e) {
            this.logger.debug('Invalid/expired JWT redirecting to Email Verification page.', e);
        }

        const discountCode: string = this.getSearchParam(currentUrl, "discountCode", storedConfig.discountCode, 32);
        storedConfig.discountCode = discountCode;
        this.soldoCookieService.storeConfig(storedConfig);
        const initData: WebappInitializationData = {
            offerName: offerName,
            planName: planName,
            hasDiscountCode: Boolean(discountCode),
            languageResolvedFrom: this.i18nService.languageResolvedFrom,
        };
        this.gtmService.trackRegistrationWebappInit(initData);
        this.amplitudeService.trackWebappInitialized(initData);

        if (applications?.length > 0) {
            this.setInitialNavigation('auth/applications').then(() => {
                this.dynatrace.leaveAction(startupActionId);
            });
            return;
        } else {
            this.registrationService.setPlanAndOffer(planName, offerName);
            this.setInitialNavigation('auth/email-verification').then(() => {
                this.dynatrace.leaveAction(startupActionId);
            });
            return;
        }
    }

    private initiateSignupWithXero(plan: string, offer: string): Promise<void> {
        this.soldoCookieService.writeSignupWithXeroCookie(plan, offer);

        const initActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.STARTUP_INIT_XERO_OAUTH);

        return this.xeroIntegrationService.start().then(xeroOAuthDTO => {
            this.dynatrace.leaveAction(initActionId);

            const xeroLoginUrl = new URL(this.config.signupWithXeroLoginURLRoot);
            xeroLoginUrl.searchParams.append("response_type", "code");
            xeroLoginUrl.searchParams.append("client_id", xeroOAuthDTO.clientId);
            xeroLoginUrl.searchParams.append("state", xeroOAuthDTO.state);
            xeroLoginUrl.searchParams.append("redirect_uri", this.config.signupWithXeroLoginRedirectURI);
            xeroLoginUrl.searchParams.append("scope", this.config.signupWithXeroLoginAuthorizationScopes);

            location.replace(xeroLoginUrl.toString());
        });
    }

    private async startupWithXeroOpenID(xeroStatus: XeroStatus): Promise<string> {
        const signupWithXeroConf = this.soldoCookieService.readSignupWithXeroCookie();
        this.registrationService.setApplicationSource(this.config.signupWithXeroLeadSource as ApplicationSource);

        const xeroOAuthDTO: XeroOAuthDTO = {
            exchangeCode: xeroStatus.code,
            state: xeroStatus.state,
            redirectUri: this.config.signupWithXeroLoginRedirectURI,
            reference: xeroStatus.reference
        };

        const application = {
            planName: signupWithXeroConf.plan,
            offerName: signupWithXeroConf.offer,
            company: {},
            webappCheckList: '',
            segment: ApplicationSegment.selfServe
        };
        const initData: WebappInitializationData = {
            offerName: signupWithXeroConf.plan,
            planName: signupWithXeroConf.offer,
            isXero: true,
            languageResolvedFrom: this.i18nService.languageResolvedFrom
        };
        this.gtmService.trackRegistrationWebappInit(initData);
        this.amplitudeService.trackWebappInitialized(initData);
        const xeroOpenIDActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.STARTUP_READ_XERO_OPENID);

        // send code and state to get OpenID data
        await this.xeroIntegrationService.getData(xeroOAuthDTO);

        this.registrationService.setPlanAndOffer(signupWithXeroConf.plan, signupWithXeroConf.offer);
        this.amplitudeService.trackSignupWithXeroAuthorized();

        const resumeRoute = await this.registrationService.initRegistration(application as Application);

        this.dynatrace.leaveAction(xeroOpenIDActionId);
        this.gtmService.trackNewRegistrationStarted(application.planName, application.offerName, application.segment);
        this.amplitudeService.trackNewRegistrationStarted(application.segment);

        return resumeRoute;
    }

    private getSearchParam(urlObject: URL, key: string, defaultValue: string, maxLength: number = 512): string {
        if (urlObject?.searchParams) {
            const value = urlObject.searchParams.get(key);
            return value === "" || value === null || value.length > maxLength ? defaultValue : value;
        }
        return defaultValue;
    }

    private setInitialNavigation(path: string): Promise<any> {
        return this.router.navigate([path], { replaceUrl: true });
    }

    private initializeAnalyticsServices() {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe((e: NavigationEnd) => {
            const snapshot = this.router.routerState.snapshot;
            let activeSnapshot: ActivatedRouteSnapshot = snapshot.root.firstChild;
            while (activeSnapshot.firstChild) {
                activeSnapshot = activeSnapshot.firstChild;
            }

            this.gtmService.onNavigationEnd(activeSnapshot);
            this.intercomIntegrationService.onNavigationEnd();
            this.amplitudeService.trackPageView(activeSnapshot);
            this.dynatrace.setPage(e.urlAfterRedirects);
        });

        this.gtmService.init();
        this.amplitudeService.initAmplitude(packageObject.version);
    }

    private decodeApplicationSegmentParameter(segment: string): ApplicationSegment {
        return segment === 'd' ? ApplicationSegment.direct : ApplicationSegment.selfServe;
    }

    private initializeMarketingData(currentUrl: URL) {
        const marketingData: MarketingData = {
            marketoCookieId: this.marketoIntegrationService.getMarketoCookie(),
            destinationId: this.getSearchParam(currentUrl, "destinationid", null, 64)
        };

        // Google Click ID
        const gclidData = this.soldoCookieService.readDedicatedGoogleClickIDCookie();
        if (gclidData !== null) {
            marketingData.googleData = {
                clickId: gclidData.gclid,
                clickTimestamp: `${gclidData.gclidTimestamp}`
            };
        }

        // UTM Parameters
        const UtmParams = this.soldoCookieService.readDedicatedUTMCookie();
        if (UtmParams !== null) {
            marketingData.utmData = {
                campaign: UtmParams.utm_campaign,
                source: UtmParams.utm_source,
                content: UtmParams.utm_content,
                medium: UtmParams.utm_medium,
                term: UtmParams.utm_term
            };
            marketingData.driverId = UtmParams.driverid;
        }

        // Affiliate Marketing parameters
        const affiliateData = this.soldoCookieService.updateAffiliateData(currentUrl.searchParams);
        try {
            if (affiliateData !== null) {
                marketingData.affiliateData = {
                    id: affiliateData.affiliateId,
                    clickTime: `${affiliateData.affiliateClickTime}`,
                    metadata: JSON.parse(affiliateData.affiliateMetadata)
                }
            }
        } catch (e) {
            this.logger.trace('Affiliate parsing failed with data: ', affiliateData);
        }

        return marketingData;
    }

    private recordCookiePreferences() {
        this.pushAPI.promise('gtm-consent-ready').then((preferencesEvent) => {
            try {
                this.registrationService.setCookiePreferences(JSON.stringify(preferencesEvent.value));
            } catch (e) {
                this.logger.warn("Can't serialize cookie preferences object", e);
            }
        });
    };
}
